import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { scheduleGoalMorningNotification } from "../services/goalNotifications";
import {
  ArrowLeft,
  Target,
  Dumbbell,
  Heart,
  Brain,
  Flame,
  BookOpen,
  Sparkles,
  Loader2,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Calendar,
  RefreshCw,
} from "lucide-react";

const ICON_MAP = {
  dumbbell: Dumbbell,
  heart: Heart,
  brain: Brain,
  flame: Flame,
};

const VERDICT_META = {
  ottimo: { label: "Ottimo", color: "#10b981", bg: "rgba(16,185,129,0.15)" },
  buono: { label: "Buono", color: "#22d3ee", bg: "rgba(34,211,238,0.15)" },
  da_migliorare: {
    label: "Da migliorare",
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.15)",
  },
  insufficiente: {
    label: "Insufficiente",
    color: "#ef4444",
    bg: "rgba(239,68,68,0.15)",
  },
};

function ScoreRing({ score, color = "#a78bfa", size = 96 }) {
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score || 0));
  const offset = c - (pct / 100) * c;
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke="rgba(255,255,255,0.1)"
        strokeWidth={stroke}
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={c}
        strokeDashoffset={offset}
        strokeLinecap="round"
        fill="none"
        style={{ transition: "stroke-dashoffset 0.8s ease-out" }}
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dy=".35em"
        transform={`rotate(90 ${size / 2} ${size / 2})`}
        className="fill-white font-semibold"
        style={{ fontSize: size * 0.28 }}
      >
        {score != null ? Math.round(score) : "—"}
      </text>
    </svg>
  );
}

function DimensionRow({ dim }) {
  const s = dim.score || 0;
  const color =
    s >= 85 ? "#10b981" : s >= 70 ? "#22d3ee" : s >= 50 ? "#f59e0b" : "#ef4444";
  return (
    <div
      data-testid={`goal-dimension-${dim.key}`}
      className="rounded-2xl border border-white/10 bg-white/5 p-3"
    >
      <div className="flex items-center justify-between">
        <div className="text-white/90 text-[13px] font-medium">{dim.label}</div>
        <div className="text-white text-[13px] font-semibold" style={{ color }}>
          {Math.round(s)}
        </div>
      </div>
      <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full transition-all"
          style={{ width: `${s}%`, background: color }}
        />
      </div>
      <div className="mt-1.5 flex items-baseline justify-between text-[11px]">
        <span className="text-white">
          {dim.value ?? "—"} {dim.unit}
        </span>
        <span className="text-white/50">target {dim.target}</span>
      </div>
      {dim.source && (
        <div className="mt-1 text-[10px] text-white/40 leading-snug">
          Fonte: {dim.source}
        </div>
      )}
    </div>
  );
}

function GoalPickerCard({ goal, onPick, saving }) {
  const Icon = ICON_MAP[goal.icon] || Target;
  return (
    <button
      data-testid={`goal-picker-${goal.key}`}
      onClick={() => onPick(goal.key)}
      disabled={saving}
      className="w-full rounded-[22px] p-4 text-left transition-transform hover:-translate-y-0.5 disabled:opacity-60 relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${goal.color}30 0%, #0d0b23 100%)`,
        border: `1px solid ${goal.color}66`,
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center"
          style={{ background: `${goal.color}30`, border: `1px solid ${goal.color}66` }}
        >
          <Icon size={22} style={{ color: goal.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white text-[15.5px] font-semibold leading-tight">
            {goal.title}
          </div>
          <div className="text-white/70 text-[12px] mt-0.5">{goal.subtitle}</div>
          <div className="text-white/50 text-[11px] mt-2 leading-snug">
            {goal.why_matters}
          </div>
        </div>
      </div>
    </button>
  );
}

export default function Obiettivi() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [goal, setGoal] = useState(null); // null=unknown, false=nessuno, obj
  const [daily, setDaily] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [showChange, setShowChange] = useState(false);

  const loadAll = useCallback(async () => {
    setErr("");
    try {
      const [catRes, meRes] = await Promise.all([
        api.get("/api/goals/catalog"),
        api.get("/api/goals/me"),
      ]);
      setCatalog(catRes.data.goals || []);
      if (meRes.data.has_goal) {
        setGoal(meRes.data.goal);
        // Carica valutazioni
        try {
          const [dRes, wRes] = await Promise.allSettled([
            api.get("/api/goals/me/daily-evaluation"),
            api.get("/api/goals/me/weekly-evaluation"),
          ]);
          if (dRes.status === "fulfilled") setDaily(dRes.value.data);
          if (wRes.status === "fulfilled") setWeekly(wRes.value.data);
        } catch {
          /* ignore */
        }
      } else {
        setGoal(false);
      }
    } catch (e) {
      setErr("Impossibile caricare gli obiettivi. Riprova.");
    }
  }, [api]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const pickGoal = async (goalKey) => {
    setSaving(true);
    setErr("");
    try {
      // Timeout esteso: la generazione del piano AI può richiedere 10-15s.
      // L'axios instance globale ha timeout 12s che aborterebbe la richiesta
      // client-side anche se il server completa correttamente.
      await api.post(
        "/api/goals/me",
        { goal_key: goalKey },
        { timeout: 60000 }
      );
      setShowChange(false);
      await loadAll();
      // Best-effort: pianifica notifica mattutina (silent no-op su web)
      scheduleGoalMorningNotification().catch(() => {});
    } catch (e) {
      setErr(
        e?.response?.data?.detail ||
          "Errore durante l'impostazione dell'obiettivo. Riprova."
      );
    } finally {
      setSaving(false);
    }
  };

  // -------- Loading --------
  if (catalog === null || goal === null) {
    return (
      <div className="min-h-screen bg-[#0b0a1f] flex items-center justify-center">
        <Loader2 className="animate-spin text-violet-300" size={26} />
      </div>
    );
  }

  // -------- Nessun obiettivo → wizard singolo step --------
  if (goal === false || showChange) {
    return (
      <div className="min-h-screen bg-[#0b0a1f] pb-24">
        <header className="sticky top-0 z-10 backdrop-blur-md bg-[#0b0a1f]/80 border-b border-white/5 px-4 py-3 flex items-center gap-3">
          <button
            data-testid="obiettivi-back"
            onClick={() => (showChange ? setShowChange(false) : navigate(-1))}
            className="w-9 h-9 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-white/80"
            aria-label="Indietro"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-white text-[17px] font-semibold">
            {showChange ? "Cambia obiettivo" : "Il tuo obiettivo"}
          </h1>
        </header>

        <div className="px-4 pt-4">
          <div className="rounded-2xl border border-violet-500/25 bg-violet-500/10 p-3.5 flex items-start gap-2">
            <Sparkles size={16} className="text-violet-300 mt-0.5" />
            <div className="text-white/80 text-[12.5px] leading-snug">
              Scegli l'obiettivo più importante per te ora. Costruiremo un piano
              personalizzato basato su <b className="text-white">fonti scientifiche verificate</b>{" "}
              (National Sleep Foundation, AASM, WHO, PubMed).
            </div>
          </div>

          {err && (
            <div
              data-testid="obiettivi-error"
              className="mt-3 rounded-2xl border border-rose-400/30 bg-rose-500/10 p-3 text-rose-100 text-[12.5px] flex items-start gap-2"
            >
              <AlertCircle size={14} className="mt-0.5" />
              <div>{err}</div>
            </div>
          )}

          <div className="mt-4 space-y-3">
            {catalog.map((g) => (
              <GoalPickerCard
                key={g.key}
                goal={g}
                onPick={pickGoal}
                saving={saving}
              />
            ))}
          </div>

          {saving && (
            <div
              data-testid="obiettivi-generating"
              className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-3 flex items-center gap-2 text-white/80 text-[12.5px]"
            >
              <Loader2 size={16} className="animate-spin text-violet-300" />
              Sto costruendo il tuo piano personalizzato…
            </div>
          )}
        </div>
      </div>
    );
  }

  // -------- Obiettivo attivo → dashboard --------
  const Icon = ICON_MAP[goal.goal_snapshot?.icon] || Target;
  const color = goal.goal_snapshot?.color || "#7C3AED";
  const plan = goal.plan_ai_cached;
  const isFallback = plan?.source === "kb_only";
  const dailyVerdict = daily?.has_data ? VERDICT_META[daily.verdict] : null;
  const weeklyVerdict = weekly?.has_data ? VERDICT_META[weekly.verdict] : null;

  return (
    <div className="min-h-screen bg-[#0b0a1f] pb-32">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-[#0b0a1f]/80 border-b border-white/5 px-4 py-3 flex items-center gap-3">
        <button
          data-testid="obiettivi-back"
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-white/80"
          aria-label="Indietro"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-white text-[17px] font-semibold flex-1">Obiettivi</h1>
        <button
          data-testid="obiettivi-change"
          onClick={() => setShowChange(true)}
          className="text-[12px] text-violet-300 flex items-center gap-1"
        >
          <RefreshCw size={12} /> Cambia
        </button>
      </header>

      <div className="px-4 pt-4">
        {/* Header goal card */}
        <div
          data-testid="obiettivi-header"
          className="rounded-[24px] p-4 relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${color}30 0%, #0d0b23 90%)`,
            border: `1px solid ${color}55`,
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{ background: `${color}33`, border: `1px solid ${color}66` }}
            >
              <Icon size={26} style={{ color }} />
            </div>
            <div>
              <div className="text-white/60 text-[11px] uppercase tracking-[0.12em]">
                Obiettivo attivo
              </div>
              <div className="text-white text-[18px] font-semibold leading-tight">
                {goal.goal_snapshot?.title}
              </div>
              <div className="text-white/70 text-[12px] mt-0.5">
                {goal.goal_snapshot?.subtitle}
              </div>
            </div>
          </div>
        </div>

        {/* Score Ring giornaliero */}
        <section className="mt-5">
          <div className="text-white/80 text-[13px] font-semibold mb-2 flex items-center gap-1.5">
            <Calendar size={14} /> Ieri notte
          </div>

          {!daily?.has_data ? (
            <div
              data-testid="obiettivi-no-daily"
              className="rounded-2xl border border-white/10 bg-white/5 p-4 text-white/60 text-[12.5px]"
            >
              Ancora nessuna sessione di sonno registrata. Avvia il monitoraggio
              da Buonanotte per vedere la valutazione.
            </div>
          ) : (
            <div
              data-testid="obiettivi-daily-block"
              className="rounded-[22px] border border-white/10 bg-white/[0.04] p-4"
            >
              <div className="flex items-center gap-4">
                <ScoreRing score={daily.overall_score} color={color} />
                <div className="flex-1">
                  <div
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium"
                    style={{
                      background: dailyVerdict?.bg,
                      color: dailyVerdict?.color,
                    }}
                  >
                    {dailyVerdict?.label}
                  </div>
                  <div className="text-white/70 text-[12px] mt-2 leading-snug">
                    Valutazione ancorata a{" "}
                    <b className="text-white/90">parametri scientifici</b> (NSF, AASM).
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {daily.dimensions?.map((d) => (
                  <DimensionRow key={d.key} dim={d} />
                ))}
              </div>

              {daily.tip && (
                <div
                  data-testid="obiettivi-daily-tip"
                  className="mt-4 rounded-2xl p-3.5 relative overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${color}25 0%, ${color}10 100%)`,
                    border: `1px solid ${color}44`,
                  }}
                >
                  <div className="flex items-start gap-2">
                    <Sparkles
                      size={16}
                      style={{ color }}
                      className="mt-0.5 flex-shrink-0"
                    />
                    <div>
                      <div className="text-white text-[13px] font-semibold">
                        {daily.tip.title}
                      </div>
                      <div className="text-white/80 text-[12px] mt-1 leading-snug">
                        {daily.tip.text}
                      </div>
                      {daily.tip.source && (
                        <div className="text-white/40 text-[10px] mt-1.5">
                          Fonte: {daily.tip.source}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Weekly */}
        <section className="mt-5">
          <div className="text-white/80 text-[13px] font-semibold mb-2 flex items-center gap-1.5">
            <BarChart3 size={14} /> Ultimi 7 giorni
          </div>
          {!weekly?.has_data ? (
            <div
              data-testid="obiettivi-no-weekly"
              className="rounded-2xl border border-white/10 bg-white/5 p-4 text-white/60 text-[12.5px]"
            >
              Servono più sessioni di sonno per un quadro settimanale.
            </div>
          ) : (
            <div
              data-testid="obiettivi-weekly-block"
              className="rounded-[22px] border border-white/10 bg-white/[0.04] p-4"
            >
              <div className="flex items-center gap-4">
                <ScoreRing score={weekly.overall_score} color={color} size={80} />
                <div className="flex-1">
                  <div
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium"
                    style={{
                      background: weeklyVerdict?.bg,
                      color: weeklyVerdict?.color,
                    }}
                  >
                    {weeklyVerdict?.label}
                  </div>
                  <div className="text-white/70 text-[12px] mt-1.5">
                    {weekly.n_sessions} sessioni analizzate
                  </div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {weekly.dimensions?.map((d) => (
                  <DimensionRow key={d.key} dim={d} />
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Piano AI cached */}
        {plan && (
          <section className="mt-5">
            <div className="text-white/80 text-[13px] font-semibold mb-2 flex items-center gap-1.5">
              <BookOpen size={14} /> Il tuo piano personalizzato
              {isFallback ? (
                <span className="text-white/40 text-[10px] font-normal">
                  · fonte: knowledge base
                </span>
              ) : (
                <span className="text-emerald-300/70 text-[10px] font-normal flex items-center gap-1">
                  <CheckCircle2 size={10} /> generato con AI · fonti verificate
                </span>
              )}
            </div>
            <div
              data-testid="obiettivi-plan"
              className="rounded-[22px] border border-white/10 bg-white/[0.04] p-4"
            >
              {plan.intro && (
                <div className="text-white/85 text-[13px] leading-snug italic">
                  {plan.intro}
                </div>
              )}
              <div className="mt-3 space-y-2.5">
                {plan.weekly_habits?.map((h, i) => (
                  <div
                    key={i}
                    data-testid={`obiettivi-habit-${i}`}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-3"
                  >
                    <div className="flex items-start gap-2">
                      <div
                        className="w-6 h-6 rounded-lg flex items-center justify-center text-[11px] font-semibold flex-shrink-0"
                        style={{ background: `${color}30`, color }}
                      >
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        <div className="text-white text-[13px] font-semibold">
                          {h.title}
                        </div>
                        <div className="text-white/80 text-[12px] mt-1 leading-snug">
                          {h.text}
                        </div>
                        {h.source && (
                          <div className="text-white/40 text-[10px] mt-1.5">
                            Fonte: {h.source}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
